import { create } from "zustand";
import { api } from "@shared/api";
import type { ConnectionState } from "@shared/realtime";
import type {
  CallRing,
  DirectChannel,
  PublicUser,
  ServerState,
  ServerSummary,
  Uuid,
} from "@shared/api";

/**
 * Кто сидит в голосовом канале и что он там делает.
 *
 * Признаки идут вместе с самим составом, а не отдельным запросом: они и есть
 * состав — «кто в канале» без «с включённым ли микрофоном» никому не нужно.
 *
 * Взяты они с сервера, а не из LiveKit, и это единственный возможный путь:
 * тот знает про дорожки только у тех, кто в комнате, а список под каналом
 * видят все, кто видит канал.
 */
export interface CallMember {
  id: Uuid;
  /** Микрофон включён и слышен. */
  microphone: boolean;
  /** Звук выключен у себя. */
  deafened: boolean;
  /** Показывает экран. */
  live: boolean;
  /** Заглушён на сервере модератором. */
  serverMuted?: boolean;
  /** Оглушён на сервере модератором. */
  serverDeafened?: boolean;
  /** Выступает на сцене; у голосового канала всегда нет. */
  speaker?: boolean;
  /** Когда поднял руку на сцене; `null` — опущена. */
  hand?: string | null;
}

/**
 * Что где есть и кто где: серверы, переписки, связи, состав звонков, связь.
 *
 * Первая половина переезда со `StoreProvider`. Сюда взято то, что просто
 * кладёт ответ сервера в состояние, — без хитростей с окнами ленты и
 * отметками прочтения: те живут в `store.tsx` и переедут отдельно.
 *
 * Стор, а не контекст, ровно затем, зачем и сессия: обработчики событий сокета
 * живут одной подпиской на всё время работы, и дотягиваться до состояния им
 * приходилось через зеркала. Здесь они зовут действия напрямую.
 */
interface Catalogue {
  servers: ServerSummary[];
  states: Record<Uuid, ServerState | undefined>;
  directs: DirectChannel[];
  relationships: PublicUser[];
  /** Кто сейчас на связи. Приходит событиями, поэтому держится отдельно. */
  online: Set<Uuid>;
  /** Состав звонков по каналам. */
  calls: Record<Uuid, CallMember[]>;
  /**
   * Кого вызывают в звонок переписки и до какого времени — как в Discord: звонит,
   * пока вызванный не вошёл, не отклонил или не вышел срок. Решает сервер:
   * клиент, выводивший звонок из состава, звонил вышедшему заново.
   */
  rings: Record<Uuid, CallRing[]>;
  connection: ConnectionState;
  attempt: number;
  /**
   * С какого сервера нас только что убрали и был ли это бан.
   *
   * Держится до тех пор, пока человек не закроет сообщение: узнать об
   * исключении он должен наверняка, а не мельком — окно могло быть свёрнуто,
   * когда это случилось.
   */
  removedFrom: { serverId: Uuid; banned: boolean } | null;
  /**
   * Модератор велел перейти в другой голосовой канал. Переходит звонок сам,
   * обычным входом; отметку снимает тот, кто её исполнил.
   */
  movedTo: { from: Uuid; to: Uuid } | null;
  /**
   * Попросили войти в звонок — откуда-то, где самого звонка нет: строка
   * «присоединиться к звонку» в ленте, «принять» в окне вызова. Входит тот,
   * кто держит звонок, и снимает просьбу.
   */
  callRequest: Uuid | null;

  reloadServers: () => Promise<void>;
  reloadDirects: () => Promise<void>;
  reloadRelationships: () => Promise<void>;
  loadCall: (channelId: Uuid) => Promise<void>;
  loadServer: (id: Uuid) => Promise<ServerState | undefined>;

  /** Связь сменилась: состояние и номер попытки приходят вместе. */
  setLink: (state: ConnectionState, tries: number) => void;
  /** Кто-то появился или пропал. */
  setPresence: (userId: Uuid, online: boolean) => void;
  /** Кто-то вошёл в голосовой канал или вышел из него. */
  setVoice: (
    channelId: Uuid,
    userId: Uuid,
    left: boolean,
    restriction?: { serverMuted: boolean; serverDeafened: boolean },
  ) => void;
  /** У кого-то в звонке переключились микрофон, звук или показ. */
  setVoiceState: (channelId: Uuid, member: CallMember) => void;
  /** Кого вызывают в звонок переписки — список целиком, прежний заменяется. */
  setRings: (channelId: Uuid, ringing: CallRing[]) => void;
  /** Нас убрали с сервера. `null` — сообщение прочитано и закрыто. */
  setRemovedFrom: (removed: { serverId: Uuid; banned: boolean } | null) => void;
  setMovedTo: (move: { from: Uuid; to: Uuid } | null) => void;
  requestCall: (channelId: Uuid | null) => void;

  /**
   * Забыть всё.
   *
   * Прежде это делалось само: `StoreProvider` монтировался после входа и
   * уходил при выходе вместе со всем содержимым. Стор живёт в модуле и сам не
   * уйдёт, поэтому чистка стала явной — иначе следующий вошедший увидел бы
   * чужие серверы и чужие переписки.
   */
  reset: () => void;
}

const EMPTY = {
  servers: [] as ServerSummary[],
  states: {} as Record<Uuid, ServerState | undefined>,
  directs: [] as DirectChannel[],
  removedFrom: null as { serverId: Uuid; banned: boolean } | null,
  movedTo: null as { from: Uuid; to: Uuid } | null,
  callRequest: null as Uuid | null,
  relationships: [] as PublicUser[],
  online: new Set<Uuid>(),
  calls: {} as Record<Uuid, CallMember[]>,
  rings: {} as Record<Uuid, CallRing[]>,
  connection: "connecting" as ConnectionState,
  attempt: 0,
};

/** Сколько звонит вызов — столько же, сколько на сервере (`CallRing.Duration`). */
export const RING_MS = 30_000;

/**
 * Срок вызова — по своим часам. Сервер шлёт срок своими, а часы машины бывают
 * впереди: срок, уже прошедший по ним, погасил бы вызов, не дав ему позвонить.
 * Новый вызов (или продлённый «позвать ещё раз») звонит полный срок от
 * получения; уже известный сохраняет свой.
 */
export function localRings(known: CallRing[] | undefined, incoming: CallRing[]): CallRing[] {
  return incoming.map((ring) => {
    const was = known?.find((item) => item.user_id === ring.user_id);
    if (was && was.server === ring.until) return was;
    // Сервер присылает только живые вызовы: «уже прошёл» по своим часам значит
    // «часы спешат» — тогда звонит весь срок.
    const left = Math.min(RING_MS, Math.max(0, Date.parse(ring.until) - Date.now()));
    const local = Date.now() + (left || RING_MS);
    return { user_id: ring.user_id, until: new Date(local).toISOString(), server: ring.until };
  });
}

export const useCatalogue = create<Catalogue>()((set, get) => ({
  ...EMPTY,

  reloadServers: async () => {
    set({ servers: await api.servers.list() });
  },

  reloadDirects: async () => {
    const directs = (await api.directs.list()).items;
    set((state) => ({
      directs,
      rings: {
        ...state.rings,
        ...Object.fromEntries(
          directs.map((direct) => [direct.channel_id, localRings(state.rings[direct.channel_id], direct.ringing ?? [])]),
        ),
      },
    }));
    // Идущий звонок переписки — составом из учёта, как у голосового канала: по нему звонят.
    for (const direct of directs) {
      if ((direct.in_call?.length ?? 0) > 0) void get().loadCall(direct.channel_id);
    }
  },

  reloadRelationships: async () => {
    const items = (await api.relationships.list()).items;
    set((state) => {
      const online = new Set(state.online);
      for (const user of items) {
        if (user.online) online.add(user.id);
        else online.delete(user.id);
      }
      return { relationships: items, online };
    });
  },

  loadCall: async (channelId) => {
    const { items, ringing } = await api.voice.participants(channelId);
    set((state) => ({
      rings: ringing ? { ...state.rings, [channelId]: localRings(state.rings[channelId], ringing) } : state.rings,
      calls: {
        ...state.calls,
        [channelId]: items.map((item) => ({
          id: item.user_id,
          microphone: item.microphone,
          deafened: item.deafened,
          live: item.live,
          serverMuted: !!item.server_muted,
          serverDeafened: !!item.server_deafened,
          speaker: !!item.speaker,
          hand: item.hand ?? null,
        })),
      },
    }));
  },

  loadServer: async (id) => {
    const state = await api.servers.state(id);
    set((previous) => ({ states: { ...previous.states, [id]: state } }));

    /*
     * Состав голосовых каналов набирается здесь же, а не только событиями.
     *
     * Событие приходит на вход и на выход — то есть рассказывает лишь о том,
     * что случилось при нас. Кто сидел в канале до того, как мы открыли
     * приложение, не приходит ниоткуда, и такой человек не показывается
     * вовсе: список под каналом остаётся пустым, будто там никого нет.
     */
    for (const channel of state.channels) {
      // Сцена — тоже звонок: состав нужен и ей.
      if (channel.channel_type === "voice" || channel.channel_type === "stage") void get().loadCall(channel.id);
    }

    return state;
  },

  setLink: (connection, attempt) => set({ connection, attempt }),

  setPresence: (userId, online) =>
    set((state) => {
      if (state.online.has(userId) === online) return state;
      const next = new Set(state.online);
      if (online) next.add(userId);
      else next.delete(userId);
      return { online: next };
    }),

  setVoice: (channelId, userId, left, restriction) =>
    set((state) => {
      const known = state.calls[channelId] ?? [];

      /*
       * Вошедший приходит молчащим: событие о входе несёт те же признаки, что
       * записал сервер, а настоящее своё состояние клиент присылает следом,
       * когда подключится к комнате. До этого мгновения он и правда ничего не
       * передаёт.
       */
      const next = left
        ? known.filter((member) => member.id !== userId)
        : known.some((member) => member.id === userId)
          ? known
          : [...known, { id: userId, microphone: false, deafened: false, live: false, ...restriction }];

      return { calls: { ...state.calls, [channelId]: next } };
    }),

  setVoiceState: (channelId, member) =>
    set((state) => {
      const known = state.calls[channelId];
      // Событие о том, кого мы ещё не видели, — не повод его завести:
      // состав придёт своим событием, и там же будет время входа.
      if (!known?.some((item) => item.id === member.id)) return state;

      return {
        calls: {
          ...state.calls,
          // Признаки модератора и сцены событие о своём состоянии не несёт — их не теряем.
          [channelId]: known.map((item) =>
            item.id === member.id
              ? {
                  ...member,
                  serverMuted: member.serverMuted ?? item.serverMuted,
                  serverDeafened: member.serverDeafened ?? item.serverDeafened,
                  speaker: member.speaker ?? item.speaker,
                  hand: member.hand !== undefined ? member.hand : item.hand,
                }
              : item,
          ),
        },
      };
    }),

  setRings: (channelId, ringing) =>
    set((state) => ({ rings: { ...state.rings, [channelId]: localRings(state.rings[channelId], ringing) } })),

  setRemovedFrom: (removed) => set({ removedFrom: removed }),
  setMovedTo: (move) => set({ movedTo: move }),
  requestCall: (channelId) => set({ callRequest: channelId }),

  reset: () => set({ ...EMPTY, online: new Set<Uuid>() }),
}));
